import { Archive, FolderInput } from 'lucide-react';
import { useState, type KeyboardEvent, type ReactNode } from 'react';
import { PEOPLE_SEARCH_MIN, PIPELINE_STAGE_LABELS, type AssignMailInput, type PersonMatch } from '@nest-recruit/shared';
import { Avatar } from '../../../../components/Avatar';
import { Button } from '../../../../components/Button';
import { SearchInput } from '../../../../components/Input';
import { SkeletonLines } from '../../../../components/Skeleton';
import { ToneChip } from '../../../../components/Tone';
import { EMPTY } from '../../../../lib/format';
import { useSettledValue } from '../../../../lib/use-settled-value';
import { useInboxPeople } from '../../api/communication-api';
import styles from './FilingPanel.module.css';

/** 06-qol §6: type-ahead waits 250 ms. */
const SEARCH_SETTLE_MS = 250;

/** One place an email can be filed: a person's profile, or one of their applications. */
export interface FilingOption {
  readonly key: string;
  readonly target: AssignMailInput;
  readonly personName: string;
  /** "Junior merchandiser · Nest Textiles · Screening", or "Their profile". */
  readonly label: string;
  readonly isOpen: boolean;
}

export function filingOptionsOf(match: PersonMatch): readonly FilingOption[] {
  const name = match.candidate.fullName;
  return [
    ...(match.mayFileOnPerson ? [{ key: `${match.candidate.id}:person`, target: { candidateId: match.candidate.id, applicationId: null }, personName: name, label: 'Their profile', isOpen: true }] : []),
    ...match.applications.map((application) => ({
      key: application.id,
      target: { candidateId: match.candidate.id, applicationId: application.id },
      personName: name,
      label: `${application.jobTitle} · ${application.ventureName} · ${PIPELINE_STAGE_LABELS[application.stage]}${application.isOpen ? '' : ' · closed'}`,
      isOpen: application.isOpen,
    })),
  ];
}

/**
 * The choice made for you: the first person found (the sender's own address comes first), on their one open
 * application if they have exactly one — the same rule the mailbox reader uses (Q8) — else on their profile.
 */
export function defaultFilingOption(matches: readonly PersonMatch[]): FilingOption | null {
  for (const match of matches) {
    const options = filingOptionsOf(match);
    if (options.length === 0) continue;
    const open = options.filter((option) => option.target.applicationId !== null && option.isOpen);
    const only = open.length === 1 ? open[0] : undefined;
    return only ?? options.find((option) => option.target.applicationId === null) ?? options[0] ?? null;
  }
  return null;
}

/**
 * Inbound mail's right pane (Option A): "File against", open all the time and already searching the sender's address,
 * so the right person is usually the first row. Each person's applications show with their job, venture and stage, so
 * the email can go on the one it is about (the server took `applicationId` all along). Dismiss sits under it.
 */
export function FilingPanel({
  mailId,
  sender,
  canEdit,
  onFile,
  onDismiss,
}: {
  readonly mailId: string;
  readonly sender: string;
  readonly canEdit: boolean;
  readonly onFile: (target: AssignMailInput, personName: string) => void;
  readonly onDismiss: () => void;
}): ReactNode {
  const [query, setQuery] = useState(sender.includes('@') ? sender : '');
  const settled = useSettledValue(query, SEARCH_SETTLE_MS);
  const people = useInboxPeople(canEdit ? mailId : null, settled);
  const [chosenKey, setChosenKey] = useState<string | null>(null);

  const matches = people.data ?? [];
  const options = matches.flatMap(filingOptionsOf);
  const chosen = options.find((option) => option.key === chosenKey) ?? defaultFilingOption(matches);
  const isSearching = settled.trim().length >= PEOPLE_SEARCH_MIN;

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    const at = options.findIndex((option) => option.key === chosen?.key);
    const next = options[at + (event.key === 'ArrowDown' ? 1 : -1)];
    if (!next) return;
    event.preventDefault();
    setChosenKey(next.key);
    document.getElementById(optionId(next.key))?.focus();
  };

  if (!canEdit) {
    return (
      <section className={styles.pane} aria-label="File against a person">
        <p className={styles.quiet}>You can read the waiting emails, but not file or dismiss them.</p>
      </section>
    );
  }

  return (
    <section className={styles.pane} aria-label="File against a person">
      <div className={styles.headText}>
        <h2 className={styles.title}>File against a person</h2>
        <span className={styles.quiet}>It goes on their timeline.</span>
      </div>
      <SearchInput aria-label="Find the person" placeholder="Name, email or mobile" value={query} onChange={(event) => setQuery(event.target.value)} onClear={() => setQuery('')} />

      {!isSearching ? (
        <p className={styles.quiet}>Type at least two letters of a name, an email or a mobile number.</p>
      ) : !people.data ? (
        <SkeletonLines count={3} />
      ) : matches.length === 0 ? (
        <p className={styles.quiet}>No one found. Try their name or mobile number instead.</p>
      ) : (
        <div className={styles.results} role="radiogroup" aria-label="Where to file it" onKeyDown={onKeyDown}>
          {matches.map((match) => {
            const own = filingOptionsOf(match);
            return (
              <div key={match.candidate.id} className={styles.person}>
                <div className={styles.who}>
                  <Avatar name={match.candidate.fullName} size="sm" />
                  <span className={styles.whoText}>
                    <span className={styles.name}>{match.candidate.fullName}</span>
                    <span className={styles.quiet}>{contactLine(match)}</span>
                  </span>
                  {match.isSameEmail && <ToneChip tone="done">Same email</ToneChip>}
                </div>
                {own.length === 0 ? (
                  <p className={styles.quiet}>Nothing of theirs you can file on.</p>
                ) : (
                  own.map((option) => {
                    const isChosen = option.key === chosen?.key;
                    return (
                      <button
                        key={option.key}
                        type="button"
                        id={optionId(option.key)}
                        role="radio"
                        aria-checked={isChosen}
                        tabIndex={isChosen || (chosen === null && option === options[0]) ? 0 : -1}
                        className={styles.option}
                        onClick={() => setChosenKey(option.key)}
                      >
                        <span className={styles.radio} aria-hidden />
                        <span>{option.label}</span>
                      </button>
                    );
                  })
                )}
              </div>
            );
          })}
        </div>
      )}

      <Button variant="solid" icon={FolderInput} block disabled={!chosen} onClick={() => chosen && onFile(chosen.target, chosen.personName)}>
        {chosen ? `File against ${chosen.personName}` : 'File against…'}
      </Button>
      {chosen && <span className={styles.quiet}>{chosen.target.applicationId === null ? 'On their profile, seen from each of their applications.' : `On ${chosen.label}.`}</span>}

      <div className={styles.dismiss}>
        <span className={styles.quiet}>Not for anyone — spam, a newsletter?</span>
        <Button variant="quiet" size="sm" icon={Archive} onClick={onDismiss}>
          Dismiss
        </Button>
      </div>
    </section>
  );
}

/** Email and mobile; a value the viewer's role may not see is `—`, never left out (non-negotiable #5). */
export function contactLine(match: PersonMatch): string {
  const hidden = match.candidate.restrictedFields ?? [];
  const part = (field: 'email' | 'phone'): string | null => {
    if (hidden.includes(field)) return EMPTY;
    const value = match.candidate[field];
    return value === null || value === '' ? null : value;
  };
  return [part('email'), part('phone')].filter((value): value is string => value !== null).join(' · ') || EMPTY;
}

function optionId(key: string): string {
  return `filing-option-${key}`;
}
